/**
 * Slideshow avec Glide.js
 * Compatible avec les classes et IDs existants du thème Dawn
 */
class SlideshowGlide {
  constructor(element) {
    this.element = element;
    this.slider = element.querySelector('[id^="Slider-"]');
    this.sliderId = this.slider ? this.slider.id : null;
    
    if (!this.slider) {
      console.warn('Slideshow: Slider container not found');
      return;
    }

    this.init();
  }

  init() {
    const autoplayEnabled = this.slider.getAttribute('data-autoplay') === 'true';
    const autoplaySpeed = parseInt(this.slider.getAttribute('data-speed')) || 5;
    const autoplayDelay = autoplaySpeed * 1000;

    // Récupérer les contrôles existants
    const prevButton = this.element.querySelector('.slider-button--prev');
    const nextButton = this.element.querySelector('.slider-button--next');
    const autoplayButton = this.element.querySelector('.slideshow__autoplay');
    const counterCurrent = this.element.querySelector('.slider-counter--current');
    const counterTotal = this.element.querySelector('.slider-counter--total');
    const counterLinks = Array.from(this.element.querySelectorAll('.slider-counter__link'));
    const sliderCounter = this.element.querySelector('.slider-counter');
    const sliderVisual = sliderCounter ? 
                        (sliderCounter.classList.contains('slider-counter--dots') ? 'dots' : 
                         sliderCounter.classList.contains('slider-counter--numbers') ? 'numbers' : 'counter') : 'counter';

    // Compter les slides
    const slides = this.slider.querySelectorAll('.glide__slide');
    const slidesCount = slides.length;

    if (slidesCount === 0) {
      console.warn('Slideshow: No slides found');
      return;
    }

    // Configuration Glide
    const glideConfig = {
      type: 'carousel',
      startAt: 0,
      perView: 1,
      gap: 0,
      autoplay: autoplayEnabled && slidesCount > 1 ? autoplayDelay : false,
      hoverpause: true,
      keyboard: true,
      dragThreshold: 10,
      swipeThreshold: 80,
      touchRatio: 1,
      touchAngle: 45,
      animationDuration: 600,
      animationTimingFunc: 'ease-in-out',
      rewind: true,
      rewindDuration: 600,
      waitForTransition: true,
      throttle: 10,
      breakpoints: {
        750: {
          perView: 1,
        },
      },
    };

    // Initialiser Glide
    this.glide = new Glide(this.slider, glideConfig);

    // Gérer les boutons de navigation personnalisés
    if (prevButton && nextButton) {
      prevButton.setAttribute('data-glide-dir', '<');
      nextButton.setAttribute('data-glide-dir', '>');
      
      // Écouter les clics
      prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.glide.go('<');
      });
      
      nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.glide.go('>');
      });
    }

    // Gérer le bouton autoplay
    this.autoplayPaused = false;
    if (autoplayButton) {
      // Vérifier l'état initial
      if (autoplayEnabled) {
        autoplayButton.classList.remove('slideshow__autoplay--paused');
        autoplayButton.setAttribute('aria-label', window.accessibilityStrings?.pauseSlideshow || 'Pause slideshow');
      } else {
        autoplayButton.classList.add('slideshow__autoplay--paused');
        autoplayButton.setAttribute('aria-label', window.accessibilityStrings?.playSlideshow || 'Play slideshow');
      }

      autoplayButton.addEventListener('click', () => {
        if (this.autoplayPaused) {
          // Reprendre
          this.glide.play();
          this.autoplayPaused = false;
          autoplayButton.classList.remove('slideshow__autoplay--paused');
          autoplayButton.setAttribute('aria-label', window.accessibilityStrings?.pauseSlideshow || 'Pause slideshow');
        } else {
          // Pause
          this.glide.pause();
          this.autoplayPaused = true;
          autoplayButton.classList.add('slideshow__autoplay--paused');
          autoplayButton.setAttribute('aria-label', window.accessibilityStrings?.playSlideshow || 'Play slideshow');
        }
      });
    }

    // Gérer les liens de pagination personnalisés
    if (sliderVisual === 'counter' && counterLinks.length > 0) {
      counterLinks.forEach((link, index) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.glide.go('=' + index);
        });
      });
    }

    // Événements Glide
    this.glide.on(['mount.after', 'run'], () => {
      this.updateCounter(counterCurrent, counterTotal);
      this.updateSlideVisibility();
      this.updateCounterLinks(counterLinks);
      this.updateNavigationButtons(prevButton, nextButton);
      this.updateAriaLabels();
    });

    // Gérer l'autoplay (play/pause) - Glide n'a pas d'événements play/pause natifs, on gère manuellement
    // L'autoplay de Glide se gère via les méthodes play() et pause()

    // Monter Glide
    this.glide.mount();

    // Stocker l'instance pour accès externe
    this.element.glideInstance = this.glide;
    
    // Méthodes de compatibilité pour le theme editor
    this.element.pause = () => this.pause();
    this.element.play = () => this.play();
  }

  updateCounter(currentEl, totalEl) {
    if (currentEl && totalEl) {
      const index = this.glide.index + 1;
      const total = this.glide.settings.perView === 1 ? this.glide.slides.length : Math.ceil(this.glide.slides.length / this.glide.settings.perView);
      currentEl.textContent = index;
      totalEl.textContent = total;
    }
  }

  updateSlideVisibility() {
    const slides = this.slider.querySelectorAll('.glide__slide');
    const activeIndex = this.glide.index;
    
    slides.forEach((slide, index) => {
      const linkElements = slide.querySelectorAll('a');
      
      if (index === activeIndex) {
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('tabindex');
        linkElements.forEach(link => link.removeAttribute('tabindex'));
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('tabindex', '-1');
        linkElements.forEach(link => link.setAttribute('tabindex', '-1'));
      }
    });
  }

  updateCounterLinks(links) {
    if (!links || links.length === 0) return;
    
    links.forEach((link, index) => {
      if (index === this.glide.index) {
        link.classList.add('slider-counter__link--active');
        link.setAttribute('aria-current', 'true');
      } else {
        link.classList.remove('slider-counter__link--active');
        link.removeAttribute('aria-current');
      }
    });
  }

  updateNavigationButtons(prevButton, nextButton) {
    const slides = this.slider.querySelectorAll('.glide__slide');
    
    if (slides.length <= 1) {
      if (prevButton) prevButton.setAttribute('disabled', 'disabled');
      if (nextButton) nextButton.setAttribute('disabled', 'disabled');
      return;
    }

    // En mode carousel (rewind), les boutons sont toujours actifs
    if (this.glide.settings.type === 'carousel') {
      if (prevButton) prevButton.removeAttribute('disabled');
      if (nextButton) nextButton.removeAttribute('disabled');
    } else {
      // Pour le mode slider, désactiver aux extrémités
      if (prevButton) {
        if (this.glide.index === 0) {
          prevButton.setAttribute('disabled', 'disabled');
        } else {
          prevButton.removeAttribute('disabled');
        }
      }
      if (nextButton) {
        if (this.glide.index === slides.length - 1) {
          nextButton.setAttribute('disabled', 'disabled');
        } else {
          nextButton.removeAttribute('disabled');
        }
      }
    }
  }

  updateAriaLabels() {
    const slides = this.slider.querySelectorAll('.glide__slide');
    const total = slides.length;
    const currentIndex = this.glide.index + 1;
    
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-label', `${currentIndex} of ${total}`);
    });
  }

  // Méthodes publiques pour compatibilité
  play() {
    if (this.glide) {
      this.glide.play();
    }
  }

  pause() {
    if (this.glide) {
      this.glide.pause();
    }
  }
}

// Initialiser tous les slideshows au chargement
document.addEventListener('DOMContentLoaded', () => {
  // Attendre que Glide soit chargé
  if (typeof Glide === 'undefined') {
    console.warn('Glide.js is not loaded');
    return;
  }

  // Attendre un peu pour s'assurer que l'ancien code ne s'est pas initialisé
  setTimeout(() => {
    const slideshows = document.querySelectorAll('slideshow-component');
    slideshows.forEach(slideshow => {
      // Vérifier si l'ancien SlideshowComponent s'est initialisé
      if (slideshow.slider && slideshow.slider.classList.contains('slider--everywhere')) {
        // Détruire toute instance existante de l'ancien système
        if (slideshow.slider.scrollLeft !== undefined) {
          // L'ancien système utilise scroll, on peut le réinitialiser
          slideshow.slider.scrollLeft = 0;
        }
      }
      new SlideshowGlide(slideshow);
    });
  }, 100);
});

// Réinitialiser lors de l'ajout dynamique de contenu (Shopify theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (typeof Glide === 'undefined') return;
    
    const slideshow = event.target.querySelector('slideshow-component');
    if (slideshow) {
      // Détruire l'instance existante si elle existe
      if (slideshow.glideInstance) {
        slideshow.glideInstance.destroy();
      }
      new SlideshowGlide(slideshow);
    }
  });

  document.addEventListener('shopify:section:reorder', (event) => {
    if (typeof Glide === 'undefined') return;
    
    const slideshows = event.target.querySelectorAll('slideshow-component');
    slideshows.forEach(slideshow => {
      if (slideshow.glideInstance) {
        slideshow.glideInstance.destroy();
      }
      new SlideshowGlide(slideshow);
    });
  });
}

// Exporter pour utilisation externe
window.SlideshowGlide = SlideshowGlide;
